import { useEffect, useRef, useState } from "react";
import { Alert, Button, Space, Spin } from "antd";
import * as pdfjs from "pdfjs-dist";

export type PosicionFirma = { pagina: number; x: number; y: number; ancho: number; alto: number };
export type CampoFirma = PosicionFirma & { id: string; tipo: "firma" | "nombre" | "texto" | "imagen"; texto?: string; tamanoFuente?: number; color?: string; png?: string };
const base = `${process.env.PUBLIC_URL || ""}/`;
pdfjs.GlobalWorkerOptions.workerSrc = `${base}pdf.worker.min.mjs?v=${pdfjs.version}`;
const limitar = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** La posición es relativa a la página visible y se conserva al cambiar su escala. */
export default function PdfFirmaEditor({ pdf, firma, posicion, onChange, onReady, onPageCount, campos = [], nombre = "", permitirUbicar = true, tipoZona = "firma", campoSeleccionadoId, onSeleccionarCampo }: {
  pdf: Blob; firma: string | null; posicion: PosicionFirma | null; onChange: (p: PosicionFirma | null) => void;
  campos?: CampoFirma[]; nombre?: string; permitirUbicar?: boolean; tipoZona?: "firma" | "nombre" | "texto" | "imagen";
  onReady?: (listo: boolean) => void;
  onPageCount?: (cantidad: number) => void;
  campoSeleccionadoId?: string | null;
  onSeleccionarCampo?: (campo: CampoFirma) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const paginaRef = useRef<HTMLDivElement>(null);
  const puntero = useRef<number | null>(null);
  const arrastre = useRef<{ ancho: number; alto: number; dx: number; dy: number } | null>(null);
  const [doc, setDoc] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamano, setTamano] = useState({ ancho: 1, alto: 1 });
  const [error, setError] = useState(false);
  const [dibujando, setDibujando] = useState(true);

  useEffect(() => {
    let activo = true;
    let tarea: ReturnType<typeof pdfjs.getDocument> | undefined;
    setDoc(null); setPagina(1); setError(false); onReady?.(false);
    // PDF.js puede tratar una URL blob como una respuesta HTTP de estado 0.
    // Se leen los bytes del blob local ya autenticado y se entregan al visor.
    pdf.arrayBuffer().then((bytes) => {
      if (!activo) return null;
      tarea = pdfjs.getDocument({ data: new Uint8Array(bytes), cMapUrl: `${base}pdfjs/cmaps/`, cMapPacked: true, standardFontDataUrl: `${base}pdfjs/standard_fonts/`, wasmUrl: `${base}pdfjs/wasm/` });
      return tarea.promise;
    }).then((p) => { if (activo && p) { setDoc(p); onPageCount?.(p.numPages); } }).catch(() => { if (activo) setError(true); });
    return () => { activo = false; if (tarea) void tarea.destroy().catch(() => {}); };
  }, [pdf, onReady, onPageCount]);

  const paginaSeleccionada = posicion?.pagina;
  useEffect(() => {
    if (campoSeleccionadoId && paginaSeleccionada && doc && paginaSeleccionada <= doc.numPages) setPagina(paginaSeleccionada);
  }, [campoSeleccionadoId, paginaSeleccionada, doc]);

  useEffect(() => {
    if (!doc) return;
    let activo = true;
    let render: ReturnType<pdfjs.PDFPageProxy["render"]> | undefined;
    setDibujando(true); setError(false); onReady?.(false);
    doc.getPage(pagina).then((p) => {
      if (!activo) return;
      if (!canvas.current) throw new Error("No se pudo mostrar la página");
      const inicial = p.getViewport({ scale: 1 });
      const escala = Math.min(1.5, 1000 / Math.max(inicial.width, inicial.height));
      const vista = p.getViewport({ scale: escala });
      const lienzo = canvas.current;
      const ctx = lienzo.getContext("2d");
      if (!ctx) throw new Error("No se pudo mostrar la página");
      lienzo.width = Math.ceil(vista.width); lienzo.height = Math.ceil(vista.height);
      setTamano({ ancho: vista.width, alto: vista.height });
      render = p.render({ canvas: lienzo, canvasContext: ctx, viewport: vista });
      return render.promise;
    }).then(() => { if (activo) { setDibujando(false); onReady?.(true); } }).catch(() => { if (activo) { setError(true); setDibujando(false); onReady?.(false); } });
    return () => { activo = false; render?.cancel(); };
  }, [doc, pagina, onReady]);

  const mover = (clientX: number, clientY: number) => {
    if (!permitirUbicar || !paginaRef.current || dibujando) return;
    const rect = paginaRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const ancho = arrastre.current?.ancho ?? (posicion?.pagina === pagina ? posicion.ancho : 0.25);
    const alto = arrastre.current?.alto ?? (posicion?.pagina === pagina ? posicion.alto : Math.min(0.2, (ancho * tamano.ancho) / (3 * tamano.alto)));
    const dx = arrastre.current?.dx ?? ancho / 2;
    const dy = arrastre.current?.dy ?? alto / 2;
    onChange({ pagina, x: limitar((clientX - rect.left) / rect.width - dx, 0, 1 - ancho), y: limitar((clientY - rect.top) / rect.height - dy, 0, 1 - alto), ancho, alto });
  };
  const cambiarPagina = (n: number) => { setPagina(n); onChange(null); };
  if (error) return <Alert type="error" showIcon message="No se pudo preparar la página para ubicar la firma" description="Vuelve a abrir el contrato o solicita otro PDF a la administración." />;
  return <div>
    <p>{permitirUbicar ? "Elige la página y toca o arrastra para ubicar la zona." : "Revisa las zonas preparadas en cada página."}</p>
    {doc && <Space><Button disabled={pagina <= 1} onClick={() => cambiarPagina(pagina - 1)}>Página anterior</Button><span>Página {pagina} de {doc.numPages}</span><Button disabled={pagina >= doc.numPages} onClick={() => cambiarPagina(pagina + 1)}>Página siguiente</Button></Space>}
    {(!doc || dibujando) && <Spin aria-label="Preparando página" />}
    <div ref={paginaRef} role="application" aria-label="Página para ubicar la firma" tabIndex={permitirUbicar ? 0 : -1}
      onPointerDown={(e) => {
        if (!permitirUbicar || dibujando) return;
        const id = (e.target as HTMLElement).closest<HTMLElement>("[data-campo-id]")?.dataset.campoId;
        const campo = id ? campos.find((c) => c.id === id) : undefined;
        puntero.current = e.pointerId;
        e.currentTarget.setPointerCapture(e.pointerId);
        if (campo && onSeleccionarCampo) {
          const rect = e.currentTarget.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          arrastre.current = { ancho: campo.ancho, alto: campo.alto, dx: (e.clientX - rect.left) / rect.width - campo.x, dy: (e.clientY - rect.top) / rect.height - campo.y };
          onSeleccionarCampo(campo);
        } else { arrastre.current = null; mover(e.clientX, e.clientY); }
      }}
      onPointerMove={(e) => { if (puntero.current === e.pointerId) mover(e.clientX, e.clientY); }}
      onPointerUp={(e) => { if (puntero.current === e.pointerId) { puntero.current = null; arrastre.current = null; } }}
      onPointerCancel={() => { puntero.current = null; arrastre.current = null; }}
      onKeyDown={(e) => {
        if (!posicion && permitirUbicar && !dibujando && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          const ancho = 0.25;
          const alto = Math.min(0.2, (ancho * tamano.ancho) / (3 * tamano.alto));
          onChange({ pagina, x: (1 - ancho) / 2, y: (1 - alto) / 2, ancho, alto });
          return;
        }
        if (!posicion || posicion.pagina !== pagina) return;
        const delta: Record<string, [number, number]> = { ArrowLeft: [-0.01, 0], ArrowRight: [0.01, 0], ArrowUp: [0, -0.01], ArrowDown: [0, 0.01] };
        if (!delta[e.key]) return;
        e.preventDefault(); const [dx, dy] = delta[e.key];
        onChange({ ...posicion, x: limitar(posicion.x + dx, 0, 1 - posicion.ancho), y: limitar(posicion.y + dy, 0, 1 - posicion.alto) });
      }}
      style={{ position: "relative", width: "100%", maxWidth: tamano.ancho, lineHeight: 0, touchAction: permitirUbicar ? "none" : "auto", cursor: permitirUbicar ? "crosshair" : "default", marginTop: 12 }}>
      <canvas ref={canvas} style={{ display: "block", width: "100%", height: "auto" }} />
      {campos.filter((c) => c.pagina === pagina).map((c) => <div key={c.id} data-campo-id={c.id} style={{ position: "absolute", zIndex: c.tipo === "firma" || c.tipo === "nombre" ? 2 : 1, left: `${c.x * 100}%`, top: `${c.y * 100}%`, width: `${c.ancho * 100}%`, height: `${c.alto * 100}%`, border: `${campoSeleccionadoId === c.id ? 2 : 1}px solid #7138e9`, background: "rgba(238,230,255,.75)", lineHeight: "normal", overflow: "hidden", pointerEvents: permitirUbicar && onSeleccionarCampo ? "auto" : "none", cursor: permitirUbicar && onSeleccionarCampo ? "grab" : "default", fontSize: 12 }}>
        {c.tipo === "firma" && firma ? <img src={firma} alt="Firma en zona preparada" style={{ width: "100%", height: "100%" }} /> : c.tipo === "imagen" && c.png ? <img src={c.png} alt="Imagen agregada por administración" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : c.tipo === "firma" ? "Firma" : c.tipo === "texto" ? <span style={{ color: c.color || "#000000", fontSize: c.tamanoFuente || 12, whiteSpace: "pre-wrap" }}>{c.texto}</span> : nombre || "Nombre del cliente"}
      </div>)}
      {permitirUbicar && !campoSeleccionadoId && posicion?.pagina === pagina && (firma && tipoZona === "firma" ? <img src={firma} alt="Firma en la posición elegida" draggable={false} style={{ position: "absolute", left: `${posicion.x * 100}%`, top: `${posicion.y * 100}%`, width: `${posicion.ancho * 100}%`, height: `${posicion.alto * 100}%`, border: "1px dashed #7138e9", pointerEvents: "none" }} /> : <div aria-label="Zona en la posición elegida" style={{ position: "absolute", left: `${posicion.x * 100}%`, top: `${posicion.y * 100}%`, width: `${posicion.ancho * 100}%`, height: `${posicion.alto * 100}%`, border: "2px dashed #7138e9", background: "rgba(238,230,255,.7)", lineHeight: "normal", pointerEvents: "none", fontSize: 12 }}>{tipoZona === "firma" ? "Firma" : tipoZona === "texto" ? "Texto" : tipoZona === "imagen" ? "Imagen" : "Nombre"}</div>)}
    </div>
    {permitirUbicar && !posicion && <small>Selecciona el lugar de la zona; con teclado, usa Enter y después las flechas.</small>}
    {permitirUbicar && posicion && <small>Puedes ajustar la posición arrastrando o usando las flechas del teclado.</small>}
  </div>;
}
